import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2, Lock, PencilLine, Plus } from 'lucide-react';
import { formatDate } from '../../utils/format';
import { formatAmount } from '../../utils/creditNoteFormat';
import { creditNotesApi } from '../../services/creditNotesApi';
import {
    reasonLabel,
    type CreditNoteCounterpartyType,
    type CreditNoteDetail,
    type CreditNoteRecourseCase,
    type CreditNoteReviewStatus,
} from '../../types/creditNotes';

/**
 * The three write panels of the detail drawer: the reason, the booking-check track and the
 * recourse case.
 *
 * THE TWO STATUS TRACKS ARE INDEPENDENT (decision 5) and the drawer keeps them visibly apart. A
 * credit note can be booked perfectly correctly and still be open against a supplier; a page
 * that merged them into one verdict would force a reviewer to choose which truth to record.
 *
 * EVERY CHANGE NEEDS A REASON, AND THE SERVER IS WHAT ENFORCES IT. The forms mark the field
 * required and refuse to submit without it, but that is a courtesy — `requireChangeReason` in
 * `shared/mutations.ts` is the rule. Same for the ERP lock on the reason: the select is disabled
 * from `reasonEditable`, and the endpoint refuses the write regardless of what the page sent.
 *
 * NOTHING ON THESE PANELS COMES FROM THE ERP. Who we are claiming from, what we expect back,
 * when to chase it, whether the booking is right — none of it exists in Business Central, in
 * [etl] or elsewhere in [Reporting]. The panel says so, because a reader who assumes a figure
 * came from the ERP will trust it differently than one who knows a colleague typed it.
 */

const INPUT =
    'h-9 w-full rounded border border-gray-300 px-2 text-sm text-gray-800 '
    + 'focus:border-blue-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500';
const LABEL = 'mb-1 block text-xs font-medium text-gray-600';

function Required() {
    return <span className="text-red-700"> · required</span>;
}

/** A save button that cannot be pressed twice and says what it is doing. */
function SaveButton({ saving, disabled, label = 'Save' }: { saving: boolean; disabled?: boolean; label?: string }) {
    return (
        <button
            type="submit"
            disabled={saving || disabled}
            className="inline-flex h-8 items-center gap-1.5 rounded bg-blue-600 px-3.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {label}
        </button>
    );
}

function GhostButton(
    { onClick, children, disabled }: { onClick: () => void; children: React.ReactNode; disabled?: boolean },
) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className="inline-flex h-8 items-center gap-1.5 rounded border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
            {children}
        </button>
    );
}

/** One choice in a segmented status row. */
function StatusChip({
    active, label, onClick, disabled,
}: { active: boolean; label: string; onClick: () => void; disabled?: boolean }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex h-8 items-center gap-1.5 rounded border px-3 text-[13px] disabled:opacity-50 ${
                active
                    ? 'border-blue-300 bg-blue-50 font-medium text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
        >
            {active && <Check className="h-3.5 w-3.5" />}
            {label}
        </button>
    );
}

function PanelError({ message }: { message: string | null }) {
    if (!message) return null;
    return (
        <p className="mt-2 flex items-start gap-1.5 rounded border border-red-200 bg-red-50 px-2.5 py-1.5 text-[13px] text-red-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{message}</span>
        </p>
    );
}

/** "Saved, and nothing moved" is a different answer from "saved" and is worth saying. */
function ChangedNote({ fields }: { fields: string[] | null }) {
    if (fields === null) return null;
    return (
        <p className="mt-2 text-[11px] text-gray-500">
            {fields.length === 0
                ? 'Saved — no field changed, so nothing was added to the trail.'
                : `Saved · ${fields.join(', ')} recorded in the trail.`}
        </p>
    );
}

interface PanelProps {
    detail: CreditNoteDetail;
    canEdit: boolean;
    /** The document's own currency — the sensible default for a claim raised against it. */
    documentCurrency: string;
    onSaved: (next: CreditNoteDetail) => void;
    save: {
        reason: (body: { reasonCode: string; comment: string | null; changeReason: string }) => Promise<CreditNoteDetail>;
        review: (body: { status: CreditNoteReviewStatus; reviewDueAt: string | null; changeReason: string }) => Promise<CreditNoteDetail>;
        recourse: (body: Record<string, unknown>) => Promise<CreditNoteDetail>;
    };
}

// ==============================================================================================
// 1. Why was this credited?
// ==============================================================================================

export function ReasonPanel({ detail, canEdit, onSaved, save }: PanelProps) {
    const {
        reason, reasonEditable, catalog, erpReasonRaw, erpReasonUnmapped, supersededUserReason,
        erpReasonIsPrefillSource,
    } = detail;

    /**
     * The ERP holds a code, and it is deliberately NOT the app's category.
     *
     * `erpReasonIsPrefillSource` is true only for `GU_Category_BIG` (dbi, bin). Everywhere else
     * the filled field is the BC standard `Reason Code`, which measurement showed is not a reason
     * vocabulary at all: `SALE` at dfr, `COMP`/`SALES` at dsp, `SALES` at dtr are posting
     * classifications, and dch used a real taxonomy (`11-RÜCHNAH`, `17-REKLAMA`, …) on 7 of 169
     * documents. Mapping any of it into the group's ten codes would put credit notes into a
     * business category nobody chose — so the plan's rule is: always SHOWN, never MAPPED.
     *
     * Without this state the panel fell through to "No reason in the ERP — record it here", which
     * on dch SC-CH2600054 (`11-RÜCHNAH`, measured 2026-09-10) is simply untrue. dch is Stefan's
     * third company; a page that denies a value the ERP visibly holds loses the reader's trust in
     * every other number on it.
     */
    const erpCodeShownNotMapped = !erpReasonIsPrefillSource && !!erpReasonRaw;

    const [editing, setEditing] = useState(false);
    const [code, setCode] = useState(reason?.reasonCode ?? '');
    const [comment, setComment] = useState(reason?.comment ?? '');
    const [changeReason, setChangeReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [changed, setChanged] = useState<string[] | null>(null);

    // The form follows the document. Without this, opening a second credit note would show the
    // first one's draft in the fields.
    useEffect(() => {
        setEditing(false);
        setCode(reason?.reasonCode ?? '');
        setComment(reason?.comment ?? '');
        setChangeReason('');
        setError(null);
        setChanged(null);
    }, [detail.companyCode, detail.documentNo, reason?.reasonCode, reason?.comment]);

    /**
     * Active codes, plus whatever is currently stored even if it has been deactivated since.
     * Dropping a stored value from its own dropdown would show the field as empty and invite a
     * reviewer to overwrite a decision they never saw.
     */
    const options = useMemo(() => {
        const list = catalog.filter(c => c.isActive || c.reasonCode === reason?.reasonCode);
        // Already ordered numerically on the leading token by the server (`SortOrder`), which is
        // why `10 ÜBRIGES` sits after `9 MENGENBONUS` and not between `1` and `2`.
        return list;
    }, [catalog, reason?.reasonCode]);

    const entry = catalog.find(c => c.reasonCode === reason?.reasonCode);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setSaving(true);
        try {
            const next = await save.reason({
                reasonCode: code,
                comment: comment.trim() || null,
                changeReason: changeReason.trim(),
            });
            setChanged(next.changedFields ?? []);
            setChangeReason('');
            setEditing(false);
            onSaved(next);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Saving the reason failed.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <section className={`rounded-lg border bg-white p-4 ${reasonEditable && !reason ? 'border-amber-200' : 'border-gray-200'}`}>
            <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Why was this credited?
                </p>
                {/* Provenance, per document and always visible — decision 2 asks for exactly this. */}
                {reason?.source === 'ERP' ? (
                    <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700">
                        <Lock className="h-3 w-3" /> From the ERP · GU_Category_BIG
                    </span>
                ) : reason?.source === 'USER' ? (
                    <span className="inline-flex items-center gap-1 rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700">
                        <PencilLine className="h-3 w-3" /> Recorded in the app
                    </span>
                ) : erpCodeShownNotMapped ? (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                        <PencilLine className="h-3 w-3" /> ERP code shown, not mapped — record the reason here
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                        <PencilLine className="h-3 w-3" /> No reason in the ERP — record it here
                    </span>
                )}
            </div>

            {erpCodeShownNotMapped && (
                <p className="mb-2.5 rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[12px] leading-4 text-gray-700">
                    The ERP's own <span className="font-mono">Reason Code</span> on this document is{' '}
                    <span className="font-mono font-medium">{erpReasonRaw}</span>. It is shown, and it is
                    deliberately not turned into the app's category: outside dbi and bin that field is a
                    posting classification (<span className="font-mono">SALE</span> at dfr,{' '}
                    <span className="font-mono">SALES</span> at dsp and dtr), not a reason. dch is the only
                    company that ever used it as one, on 7 of 169 documents. Record the group reason below
                    so this credit note stays comparable with the other companies.
                </p>
            )}

            {erpReasonUnmapped && (
                <p className="mb-2.5 rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[12px] leading-4 text-amber-800">
                    The ERP carries <span className="font-mono">{erpReasonRaw}</span>, which is not one of
                    the ten codes in the group catalogue. An eleventh value has appeared in Business
                    Central — it is shown here and counted on the list, never silently dropped, but it
                    has to be added to <span className="font-mono">CreditNoteReasonCatalog</span> before
                    it can be filtered or compared.
                </p>
            )}

            {!reasonEditable ? (
                <>
                    <p className="text-sm font-medium text-gray-900">
                        {reasonLabel(entry, reason?.reasonCode)}
                    </p>
                    <p className="mt-1.5 text-[11px] leading-4 text-gray-500">
                        dbi and bin carry the reason in the ERP, so it is shown here and not editable —
                        it is changed in Business Central. The value is the ERP's own, truncated to 20
                        characters there and not by this page; no readable long text exists in the
                        database yet, so none is invented.
                        {reason?.comment && ' The note below was added in the app.'}
                    </p>
                    {reason?.comment && (
                        <p className="mt-2 border-l-2 border-gray-200 pl-2.5 text-[13px] text-gray-700">
                            {reason.comment}
                        </p>
                    )}
                    {/* The ERP has the last word, and the decision it overruled is still named. */}
                    {supersededUserReason && (
                        <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[12px] leading-4 text-amber-800">
                            A reviewer had recorded{' '}
                            <b>{reasonLabel(catalog.find(c => c.reasonCode === supersededUserReason), supersededUserReason)}</b>{' '}
                            in the app before the ERP carried a code for this document. The ERP value
                            above is what counts now; the earlier choice stays in the trail rather than
                            being dropped.
                        </p>
                    )}
                </>
            ) : !editing ? (
                <>
                    {reason ? (
                        <>
                            <p className="text-sm font-medium text-gray-900">
                                {reasonLabel(entry, reason.reasonCode)}
                            </p>
                            {reason.comment && (
                                <p className="mt-2 border-l-2 border-gray-200 pl-2.5 text-[13px] text-gray-700">
                                    {reason.comment}
                                </p>
                            )}
                            <p className="mt-1.5 text-[11px] text-gray-400">
                                {reason.updatedBy && `Last changed by ${reason.updatedBy}`}
                                {reason.updatedAt && ` · ${formatDate(reason.updatedAt.slice(0, 10))}`}
                            </p>
                        </>
                    ) : (
                        <p className="text-[13px] leading-5 text-amber-700">
                            {/* Not "neither in the ERP nor here" when the ERP visibly holds a code:
                                the box above has just quoted it, and the two sentences would
                                contradict each other on the same screen. */}
                            {erpCodeShownNotMapped
                                ? 'No group reason recorded yet.'
                                : 'No reason recorded — neither in the ERP nor here.'}
                            {' '}The group list below is the same ten codes for every company, so dbi,
                            bin, dch and dit stay comparable.
                        </p>
                    )}
                    {canEdit && (
                        <div className="mt-3">
                            <GhostButton onClick={() => setEditing(true)}>
                                <PencilLine className="h-3.5 w-3.5" />
                                {reason ? 'Change the reason' : 'Record the reason'}
                            </GhostButton>
                        </div>
                    )}
                    <ChangedNote fields={changed} />
                </>
            ) : (
                <form onSubmit={submit}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="block">
                            <span className={LABEL}>Reason<Required /></span>
                            <select
                                value={code}
                                onChange={e => setCode(e.target.value)}
                                required
                                className={INPUT}
                            >
                                <option value="">Choose a reason…</option>
                                {options.map(c => (
                                    <option key={c.reasonCode} value={c.reasonCode}>
                                        {reasonLabel(c)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className={LABEL}>Reason for this change<Required /></span>
                            <input
                                value={changeReason}
                                onChange={e => setChangeReason(e.target.value)}
                                required
                                maxLength={1000}
                                placeholder="Why is this being recorded or changed?"
                                className={INPUT}
                            />
                        </label>
                    </div>
                    <label className="mt-3 block">
                        <span className={LABEL}>Note (optional)</span>
                        <textarea
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            maxLength={1000}
                            rows={2}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
                        />
                    </label>
                    <div className="mt-3 flex items-center gap-2">
                        <SaveButton saving={saving} disabled={!code || !changeReason.trim()} label="Save reason" />
                        <GhostButton onClick={() => { setEditing(false); setError(null); }}>Cancel</GhostButton>
                    </div>
                    <PanelError message={error} />
                </form>
            )}

            {/* There is no sub-reason control. The canvas draws one, and no vocabulary for it
                exists anywhere — not in the ERP, not in [Reporting]. Inventing a second list
                would be this page making up business vocabulary, which decision 2 reserves for
                the business. The free-text note above carries the detail until Stefan defines
                one (open question 1). */}
        </section>
    );
}

// ==============================================================================================
// 2. Booking review.
// ==============================================================================================

export function ReviewPanel({ detail, canEdit, onSaved, save }: PanelProps) {
    const { review, vocabularies } = detail;

    const [status, setStatus] = useState<CreditNoteReviewStatus>(review.status);
    const [dueAt, setDueAt] = useState(review.reviewDueAt ?? '');
    const [changeReason, setChangeReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [changed, setChanged] = useState<string[] | null>(null);

    useEffect(() => {
        setStatus(review.status);
        setDueAt(review.reviewDueAt ?? '');
        setChangeReason('');
        setError(null);
        setChanged(null);
    }, [detail.companyCode, detail.documentNo, review.status, review.reviewDueAt]);

    const dirty = status !== review.status || (dueAt || null) !== (review.reviewDueAt ?? null);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setSaving(true);
        try {
            const next = await save.review({
                status,
                reviewDueAt: dueAt || null,
                changeReason: changeReason.trim(),
            });
            setChanged(next.changedFields ?? []);
            setChangeReason('');
            onSaved(next);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Saving the booking review failed.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Booking review</p>
                <span className="text-[11px] text-gray-400">
                    {review.isDefault
                        // Open BY ABSENCE. Saying so keeps "nobody has looked at this" apart from
                        // "somebody looked and left it open", which are not the same fact.
                        ? 'Open — nobody has recorded a verdict yet'
                        : `Last changed by ${review.updatedBy ?? 'unknown'}${
                            review.updatedAt ? ` · ${formatDate(review.updatedAt.slice(0, 10))}` : ''}`}
                </span>
            </div>

            <form onSubmit={submit}>
                <div className="flex flex-wrap gap-1.5">
                    {vocabularies.reviewStatuses.map(s => (
                        <StatusChip
                            key={s.code}
                            label={s.label}
                            active={status === s.code}
                            disabled={!canEdit}
                            onClick={() => setStatus(s.code)}
                        />
                    ))}
                </div>

                {canEdit && (
                    <>
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <label className="block sm:col-span-2">
                                <span className={LABEL}>Reason for this status<Required /></span>
                                <input
                                    value={changeReason}
                                    onChange={e => setChangeReason(e.target.value)}
                                    required
                                    maxLength={1000}
                                    placeholder="e.g. Waiting on the branch"
                                    className={INPUT}
                                />
                            </label>
                            <label className="block">
                                <span className={LABEL}>Follow up on (optional)</span>
                                <input
                                    type="date"
                                    value={dueAt}
                                    onChange={e => setDueAt(e.target.value)}
                                    className={INPUT}
                                />
                            </label>
                        </div>
                        <div className="mt-3">
                            <SaveButton saving={saving} disabled={!changeReason.trim()} label="Save booking review" />
                            {!dirty && changeReason.trim() && (
                                <span className="ml-2 text-[11px] text-gray-400">
                                    Nothing has changed — saving will record the reason on the row but add
                                    nothing to the trail.
                                </span>
                            )}
                        </div>
                    </>
                )}
                <PanelError message={error} />
                <ChangedNote fields={changed} />
            </form>

            {review.changeReason && (
                <p className="mt-3 border-t border-gray-100 pt-2.5 text-[12px] text-gray-600">
                    <span className="text-gray-400">Current reason: </span>{review.changeReason}
                </p>
            )}
        </section>
    );
}

// ==============================================================================================
// 3. Recourse.
// ==============================================================================================

/** The empty form for a case that does not exist yet. */
function blankCase(currency: string): Record<string, string> {
    return {
        status: 'OPEN',
        counterpartyType: 'SUPPLIER',
        counterpartyName: '',
        vendorId: '',
        claimedAmount: '',
        claimedCurrency: currency,
        receivedAmount: '',
        receivedCurrency: currency,
        submittedOn: '',
        settledOn: '',
        reviewDueAt: '',
        hubSpotTicketId: '',
        notes: '',
    };
}

function caseToForm(c: CreditNoteRecourseCase, currency: string): Record<string, string> {
    return {
        status: c.status,
        counterpartyType: c.counterpartyType,
        counterpartyName: c.counterpartyName ?? '',
        vendorId: c.vendorId ?? '',
        claimedAmount: c.claimedAmount === null ? '' : String(c.claimedAmount),
        claimedCurrency: c.claimedCurrency ?? currency,
        receivedAmount: c.receivedAmount === null ? '' : String(c.receivedAmount),
        receivedCurrency: c.receivedCurrency ?? currency,
        submittedOn: c.submittedOn ?? '',
        settledOn: c.settledOn ?? '',
        reviewDueAt: c.reviewDueAt ?? '',
        hubSpotTicketId: c.hubSpotTicketId ?? '',
        notes: c.notes ?? '',
    };
}

export function RecoursePanel({ detail, canEdit, documentCurrency, onSaved, save }: PanelProps) {
    const { recourse, vocabularies } = detail;

    // P4 writes case 1 (open question 2 is unanswered), but reads every case a document carries,
    // so a second one created later shows up here without a change.
    const [caseNo, setCaseNo] = useState<number>(recourse[0]?.caseNo ?? 1);
    const current = recourse.find(c => c.caseNo === caseNo) ?? null;

    const [open, setOpen] = useState(recourse.length > 0);
    const [form, setForm] = useState<Record<string, string>>(
        current ? caseToForm(current, documentCurrency) : blankCase(documentCurrency));
    const [changeReason, setChangeReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [changed, setChanged] = useState<string[] | null>(null);

    useEffect(() => {
        const first = recourse[0] ?? null;
        setCaseNo(first?.caseNo ?? 1);
        setOpen(recourse.length > 0);
        setForm(first ? caseToForm(first, documentCurrency) : blankCase(documentCurrency));
        setChangeReason('');
        setError(null);
        setChanged(null);
    }, [detail.companyCode, detail.documentNo, recourse, documentCurrency]);

    const set = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }));

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setSaving(true);
        try {
            const next = await save.recourse({
                caseNo,
                status: form.status,
                counterpartyType: form.counterpartyType,
                counterpartyName: form.counterpartyName.trim() || null,
                vendorId: form.vendorId.trim() || null,
                claimedAmount: form.claimedAmount.trim() || null,
                claimedCurrency: form.claimedAmount.trim() ? form.claimedCurrency : null,
                receivedAmount: form.receivedAmount.trim() || null,
                receivedCurrency: form.receivedAmount.trim() ? form.receivedCurrency : null,
                submittedOn: form.submittedOn || null,
                settledOn: form.settledOn || null,
                reviewDueAt: form.reviewDueAt || null,
                hubSpotTicketId: form.hubSpotTicketId.trim() || null,
                notes: form.notes.trim() || null,
                changeReason: changeReason.trim(),
            });
            setChanged(next.changedFields ?? []);
            setChangeReason('');
            onSaved(next);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Saving the recourse case failed.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <section className={`rounded-lg border bg-white p-4 ${current?.isOverdue ? 'border-red-200' : 'border-blue-100'}`}>
            <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Recourse
                    {recourse.length > 1 && (
                        <span className="ml-1.5 font-normal normal-case text-gray-400">
                            · case {caseNo} of {recourse.length}
                        </span>
                    )}
                </p>
                {current?.isOverdue && (
                    <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
                        Overdue {current.daysOverdue} d
                    </span>
                )}
            </div>

            {/* More than one case on a credit note is possible by schema (CaseNo is in the key)
                and is what open question 2 is about. P4 writes case 1; if a second exists, it can
                still be read and edited here. */}
            {recourse.length > 1 && (
                <div className="mb-3 flex flex-wrap gap-1.5">
                    {recourse.map(c => (
                        <StatusChip
                            key={c.caseNo}
                            label={`Case ${c.caseNo}`}
                            active={c.caseNo === caseNo}
                            onClick={() => {
                                setCaseNo(c.caseNo);
                                setForm(caseToForm(c, documentCurrency));
                                setChangeReason('');
                            }}
                        />
                    ))}
                </div>
            )}

            {!open ? (
                <>
                    <p className="text-[13px] leading-5 text-gray-600">
                        No recourse case on this credit note. Nothing here comes from the ERP — the
                        counterparty, the amount expected back and the follow-up date exist in no system
                        Dolder runs, so this app is the only place they are recorded.
                    </p>
                    {canEdit && (
                        <div className="mt-3">
                            <GhostButton onClick={() => setOpen(true)}>
                                <Plus className="h-3.5 w-3.5" /> Open a recourse case
                            </GhostButton>
                        </div>
                    )}
                    <ChangedNote fields={changed} />
                </>
            ) : (
                <form onSubmit={submit}>
                    <div className="flex flex-wrap gap-1.5">
                        {vocabularies.recourseStatuses.map(s => (
                            <StatusChip
                                key={s.code}
                                label={s.label}
                                active={form.status === s.code}
                                disabled={!canEdit}
                                onClick={() => set('status', s.code)}
                            />
                        ))}
                    </div>
                    {/* The canvas offers "None" as a sixth button. It is the state of a document
                        with no case — not somewhere a case can go back to. Once somebody decided
                        to pursue a claim, that decision and its outcome are the record; Rejected
                        and Written off are how a claim ends without money, both with their reason
                        intact. Deleting the row would leave the trail describing a case nobody
                        can find. */}
                    {current && (
                        <p className="mt-1.5 text-[11px] text-gray-400">
                            A case cannot be removed once opened — end it as “Rejected” or “Written off”
                            so the decision stays visible.
                        </p>
                    )}

                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <label className="block">
                            <span className={LABEL}>Counterparty<Required /></span>
                            <select
                                value={form.counterpartyType}
                                onChange={e => set('counterpartyType', e.target.value as CreditNoteCounterpartyType)}
                                disabled={!canEdit}
                                className={INPUT}
                            >
                                {vocabularies.counterpartyTypes.map(t => (
                                    <option key={t.code} value={t.code}>{t.label}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block lg:col-span-2">
                            <span className={LABEL}>Who</span>
                            <input
                                value={form.counterpartyName}
                                onChange={e => set('counterpartyName', e.target.value)}
                                disabled={!canEdit}
                                maxLength={200}
                                placeholder="e.g. Arkema France S.A."
                                className={INPUT}
                            />
                        </label>
                        <label className="block">
                            <span className={LABEL}>Vendor no. (optional)</span>
                            <input
                                value={form.vendorId}
                                onChange={e => set('vendorId', e.target.value)}
                                disabled={!canEdit}
                                maxLength={50}
                                className={INPUT}
                            />
                        </label>

                        <label className="block">
                            <span className={LABEL}>Expected back</span>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.claimedAmount}
                                onChange={e => set('claimedAmount', e.target.value)}
                                disabled={!canEdit}
                                className={`${INPUT} tabular-nums`}
                            />
                        </label>
                        <label className="block">
                            {/* The currency travels with the amount, always. A claim raised in CHF
                                can be paid in EUR, and a bare number would be unaddable. */}
                            <span className={LABEL}>Currency</span>
                            <input
                                value={form.claimedCurrency}
                                onChange={e => set('claimedCurrency', e.target.value.toUpperCase())}
                                disabled={!canEdit}
                                maxLength={3}
                                className={INPUT}
                            />
                        </label>
                        <label className="block">
                            <span className={LABEL}>Received</span>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.receivedAmount}
                                onChange={e => set('receivedAmount', e.target.value)}
                                disabled={!canEdit}
                                className={`${INPUT} tabular-nums`}
                            />
                        </label>
                        <label className="block">
                            <span className={LABEL}>Currency</span>
                            <input
                                value={form.receivedCurrency}
                                onChange={e => set('receivedCurrency', e.target.value.toUpperCase())}
                                disabled={!canEdit}
                                maxLength={3}
                                className={INPUT}
                            />
                        </label>

                        <label className="block">
                            <span className={LABEL}>Claim submitted</span>
                            <input
                                type="date"
                                value={form.submittedOn}
                                onChange={e => set('submittedOn', e.target.value)}
                                disabled={!canEdit}
                                className={INPUT}
                            />
                        </label>
                        <label className="block">
                            <span className={LABEL}>Settled on</span>
                            <input
                                type="date"
                                value={form.settledOn}
                                onChange={e => set('settledOn', e.target.value)}
                                disabled={!canEdit}
                                className={INPUT}
                            />
                        </label>
                        <label className="block">
                            <span className={LABEL}>Follow up on</span>
                            <input
                                type="date"
                                value={form.reviewDueAt}
                                onChange={e => set('reviewDueAt', e.target.value)}
                                disabled={!canEdit}
                                className={INPUT}
                            />
                        </label>
                        <label className="block">
                            <span className={LABEL}>HubSpot ticket</span>
                            <input
                                value={form.hubSpotTicketId}
                                onChange={e => set('hubSpotTicketId', e.target.value)}
                                disabled={!canEdit}
                                maxLength={50}
                                placeholder="Ticket id"
                                className={INPUT}
                            />
                        </label>
                    </div>

                    <label className="mt-3 block">
                        <span className={LABEL}>Note (optional)</span>
                        <textarea
                            value={form.notes}
                            onChange={e => set('notes', e.target.value)}
                            disabled={!canEdit}
                            maxLength={2000}
                            rows={2}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-800 focus:border-blue-500 focus:outline-none disabled:bg-gray-50"
                        />
                    </label>

                    {canEdit && (
                        <>
                            <label className="mt-3 block">
                                <span className={LABEL}>Reason for this status<Required /></span>
                                <input
                                    value={changeReason}
                                    onChange={e => setChangeReason(e.target.value)}
                                    required
                                    maxLength={1000}
                                    placeholder="e.g. Claim filed with the supplier"
                                    className={INPUT}
                                />
                            </label>
                            <div className="mt-3 flex items-center gap-2">
                                <SaveButton
                                    saving={saving}
                                    disabled={!changeReason.trim()}
                                    label={current ? 'Save recourse case' : 'Create recourse case'}
                                />
                                {!current && (
                                    <GhostButton onClick={() => { setOpen(false); setError(null); }}>
                                        Cancel
                                    </GhostButton>
                                )}
                            </div>
                        </>
                    )}
                    <PanelError message={error} />
                    <ChangedNote fields={changed} />
                </form>
            )}

            {current?.hubSpotTicketId && (
                <HubSpotTicketWriteBack
                    company={detail.companyCode}
                    documentNo={detail.documentNo}
                    ticketId={current.hubSpotTicketId}
                    canEdit={canEdit}
                />
            )}

            {current && (current.claimedAmount !== null || current.receivedAmount !== null) && (
                <p className="mt-3 border-t border-gray-100 pt-2.5 text-[12px] text-gray-600">
                    <span className="text-gray-400">Expected </span>
                    {current.claimedAmount === null
                        ? '–'
                        : `${formatAmount(current.claimedAmount)} ${current.claimedCurrency ?? ''}`}
                    <span className="mx-2 text-gray-300">·</span>
                    <span className="text-gray-400">Received </span>
                    <span className={current.receivedAmount ? 'text-green-700' : undefined}>
                        {current.receivedAmount === null
                            ? '–'
                            : `${formatAmount(current.receivedAmount)} ${current.receivedCurrency ?? ''}`}
                    </span>
                </p>
            )}

            <p className="mt-3 text-[11px] leading-4 text-gray-400">
                None of these fields exists in Business Central, in [etl] or anywhere else in
                [Reporting]. This app is the only source, and every change is recorded with its reason
                in the trail below.
            </p>
        </section>
    );
}

/**
 * Write the credit note number back onto the complaint ticket.
 *
 * ONE BUTTON, AND IT TELLS THE TRUTH ABOUT WHAT IT DID. The server answers with the outcome in
 * words — written / already there / refused because the field holds something else / the feature
 * is parked — and this shows that answer verbatim rather than reducing it to a tick. "Already
 * there" and "written" are different facts, and so are "refused" and "failed".
 *
 * THE FEATURE SHIPS PARKED. `HUBSPOT_ALLOW_WRITE_CREDITNOTE` is false until the write path has
 * been verified against the TEST portal, which needs the `tickets` scope neither Service Key
 * holds yet. So the expected answer today is the guard's own 503, and the button says so up
 * front instead of letting a reader discover it as a failure.
 */
function HubSpotTicketWriteBack({ company, documentNo, ticketId, canEdit }: {
    company: string; documentNo: string; ticketId: string; canEdit: boolean;
}) {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

    async function run() {
        setBusy(true);
        setResult(null);
        try {
            const res = await creditNotesApi.writeHubSpotTicket({ company, documentNo });
            setResult({ ok: true, message: res.message });
        } catch (err) {
            setResult({ ok: false, message: err instanceof Error ? err.message : 'The write failed.' });
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="mt-3 border-t border-gray-100 pt-2.5">
            <div className="flex flex-wrap items-center gap-2">
                <GhostButton onClick={run} disabled={!canEdit || busy}>
                    {busy ? 'Writing…' : `Write ${documentNo} to ticket ${ticketId}`}
                </GhostButton>
                <span className="text-[11px] text-gray-500">
                    Fills <span className="font-mono">bc_credit_note_nr_</span> only when it is empty —
                    an existing value is never overwritten.
                </span>
            </div>
            {result && (
                <p className={`mt-2 rounded border px-2.5 py-1.5 text-[12px] leading-4 ${
                    result.ok
                        ? 'border-green-200 bg-green-50 text-green-800'
                        : 'border-amber-200 bg-amber-50 text-amber-800'
                }`}>
                    {result.message}
                </p>
            )}
        </div>
    );
}
